import './ProductCatalogFilter.scss';

import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { Button, Form, Input, Popover } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FullscreenDrawer from '@/components/general/Drawers/FullscreenDrawer';
import { useUiContext } from '@/contexts/UiContext';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { Ticket } from '@/model/Ticket/Ticket';

import useProductCatalogFilter from './useProductCatalogFilter';

interface ProductCatalogFilterProps {
  filters: any[];
  setFilters: Function;
  setCurrent: Function;
}

const ProductCatalogFilter = (props: ProductCatalogFilterProps) => {
  const { t } = useTranslation();
  const { isMobile } = useUiContext();
  const { filters, setFilters, setCurrent } = props;
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { formProps } = useForm<Ticket>({ warnWhenUnsavedChanges: false });
  const { form } = formProps;
  const { productCategorySelectProps } = useProductCatalogFilter(formProps, filters);
  const applyFilters = useCallback(
    values => {
      const filters: any[] = [];

      // Product type (siempre presente, no es nullable)
      filters.push({
        field: 'type',
        operator: 'eq',
        value: values.product_type,
      });

      // Search
      if (values.search) {
        filters.push({
          field: 'search',
          operator: 'eq',
          value: values.search,
        });
      }

      // estado de stock
      if (values.category) {
        filters.push({
          field: 'category',
          operator: 'eq',
          value: values.category,
        });
      }

      setFilters(filters, 'replace');
      setCurrent(1);
      setFilterPopoverOpen(false);
    },
    [setFilters, setFilterPopoverOpen, setCurrent],
  );
  const handleClearFilters = useCallback(() => {
    form?.resetFields();
    // Restaurar product_type al valor por defecto tras resetear
    form?.setFieldValue('product_type', PRODUCT_TYPES.PRODUCT);
    setFilters([{ field: 'type', operator: 'eq', value: PRODUCT_TYPES.PRODUCT }], 'replace');
    setCurrent(1);
    setFilterPopoverOpen(false);
  }, [form, setCurrent, setFilters]);
  const filterContent = (
    <Form
      {...formProps}
      labelAlign="left"
      style={{ marginBottom: 8 }}
      onFinish={applyFilters}
      className="ProductCatalogFilterPopover">
      <div className="ProductCatalogFilterPopoverRows">
        <Form.Item
          label={t('Tipo de contenido')}
          name="product_type"
          initialValue={PRODUCT_TYPES.PRODUCT}
          className="ProductCatalogFilterRow">
          <AntdSelect
            options={Object.values(ProductTypeStrings).map((v, index) => ({
              label: t(v),
              value: index,
            }))}
          />
        </Form.Item>
        <Form.Item label={t('Buscar')} name={'search'} className="ProductCatalogFilterRow">
          <Input allowClear placeholder={t('Buscar')} />
        </Form.Item>
        <Form.Item label={t('Categoría')} name={'category'} className="ProductCatalogFilterRow">
          <AntdSelect
            {...productCategorySelectProps}
            allowClear
            showSearch
            placeholder={t('Seleccionar categoría')}
          />
        </Form.Item>
      </div>

      <div className="ProductCatalogFilterActions">
        <Button type="default" style={{ marginLeft: 8 }} onClick={handleClearFilters}>
          {t('Limpiar')}
        </Button>
        <Button htmlType="submit" type="primary">
          {t('Buscar')}
        </Button>
      </div>
    </Form>
  );

  return (
    <div className="ProductCatalogFilter">
      {isMobile ? (
        <>
          <Button
            className={filters.length > 0 ? 'ProductCatalogFilterApplied' : ''}
            onClick={() => setFilterPopoverOpen(true)}
            icon={<FilterOutlined />}
          />
          <FullscreenDrawer
            disablePush
            title={t('Filtros')}
            open={filterPopoverOpen}
            setOpen={setFilterPopoverOpen}>
            {filterContent}
          </FullscreenDrawer>
        </>
      ) : (
        <Popover
          rootClassName="ProductCatalogFilterPopover"
          content={filterContent}
          title={t('Filtros')}
          trigger="click"
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          placement="bottomRight">
          <Button
            className={filters.length > 0 ? 'ProductCatalogFilterApplied' : ''}
            icon={filters.length > 0 ? <FilterFilled /> : <FilterOutlined />}>
            {t('Filtrar')}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default ProductCatalogFilter;
